import React, { useState, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/blueprints/ui/OSAwarePopover';
import { SmilePlus } from 'lucide-react';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import useOsDark from '@/OS/hooks/useOsDark';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface EmojiPickerPopoverProps {
  onEmojiSelect: (emojiData: EmojiClickData) => void;
  triggerClassName?: string;
}

const EmojiPickerPopover: React.FC<EmojiPickerPopoverProps> = ({ onEmojiSelect, triggerClassName }) => {
  const isDark = useOsDark();
  const [open, setOpen] = useState(false);

  const handleEmojiClick = useCallback(
    (emojiData: EmojiClickData) => {
      onEmojiSelect(emojiData);
      setOpen(false);
    },
    [onEmojiSelect]
  );

  return (
    <Popover open={open} onOpenChange={setOpen} embedded>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className={cn('h-8.5 w-8.5 flex-shrink-0', triggerClassName)}>
          <SmilePlus className="h-4 w-4" />
          <span className="sr-only">Add Emoji</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent embedded className="emoji-picker-shell z-[1200000] w-auto border-none bg-transparent p-0" side="top" align="start">
        <EmojiPicker
          onEmojiClick={handleEmojiClick}
          theme={isDark ? Theme.DARK : Theme.LIGHT}
          lazyLoadEmojis
          searchPlaceholder="Search emojis"
          skinTonesDisabled
          previewConfig={{ showPreview: false }}
          style={
            {
              '--epr-bg-color': 'hsl(var(--popover))',
              '--epr-text-color': 'hsl(var(--popover-foreground))',
              '--epr-search-input-bg-color': 'hsl(var(--background))',
              '--epr-search-input-bg-color-active': 'hsl(var(--background))',
              '--epr-search-border-color': 'hsl(var(--primary))',
              '--epr-hover-bg-color': 'hsl(var(--accent))',
              '--epr-category-label-bg-color': 'hsl(var(--popover))',
              '--epr-emoji-size': '20px',
              '--epr-emoji-gap': '8px',
            } as React.CSSProperties
          }
        />
      </PopoverContent>
    </Popover>
  );
};

export default EmojiPickerPopover;